import React from 'react';
import { Alert, Button } from '~/ui/components';
import { FilePresent, Eye, User } from '~/ui/assets/icons';
import { toHumanFileSize } from '~/common';
import { FileType } from '~/ui/components/Files/Files';
import CommentTypeBadge from '~/components/CommentTypeBadge/CommentTypeBadge';
import WhatsappPreview from '../components/WhatsappPreview';
import {
  FileItem,
  FileItemLeft,
  FileItemInfo,
  FileItemName,
  FileItemSize,
  FileItemLink,
} from '../ResolveTicket.styles';
import {
  StepBody,
  ReviewBlock,
  ReviewBlockTitle,
  ReviewRow,
  MessageCard,
  MessageCardHeader,
  MessageCardTitle,
  MessageCardBody,
  WhatsappBubble,
  Footer,
  FooterHint,
} from '../RaFlow.styles';

const RESOLVE_WARNING_RA =
  'Revise tudo antes de resolver. Ao confirmar, enviaremos a mensagem ao WhatsApp do cliente e publicaremos no Reclame Aqui a resposta pública e a mensagem reservada. Essa ação não pode ser desfeita.';

const RESOLVE_WARNING_STANDARD =
  'Revise tudo antes de resolver. Ao confirmar, enviaremos a mensagem ao WhatsApp do cliente. Essa ação não pode ser desfeita.';

type RaReviewStepProps = {
  ticketId: string;
  clientName: string;
  normalizedStatusName: string;
  hasFiles: boolean;
  agentComment: string;
  resolvingAreaName?: string;
  priorityLabel?: string;
  statusName?: string;
  justificationText?: string;
  publicMessage: string;
  reservedMessage: string;
  files?: FileType[];
  onResolve: () => void;
  isLoading?: boolean;
  // When false, the review hides the Reclame Aqui public/reserved messages and only shows
  // the WhatsApp response (standard resolution flow).
  isReclameAqui?: boolean;
};

function RaReviewStep({
  ticketId,
  clientName,
  normalizedStatusName,
  hasFiles,
  agentComment,
  resolvingAreaName,
  priorityLabel,
  statusName,
  justificationText,
  publicMessage,
  reservedMessage,
  files,
  onResolve,
  isLoading,
  isReclameAqui = true,
}: RaReviewStepProps) {
  const hasPublic = isReclameAqui && !!publicMessage.trim();
  const hasReserved = isReclameAqui && !!reservedMessage.trim();

  return (
    <StepBody>
      <Alert
        status="warning"
        icon="warning"
        label={isReclameAqui ? RESOLVE_WARNING_RA : RESOLVE_WARNING_STANDARD}
        fullWidth
      />

      <ReviewBlock>
        <ReviewBlockTitle>Resolução do ticket</ReviewBlockTitle>
        {resolvingAreaName && (
          <ReviewRow>
            <span>Área</span>
            <span>{resolvingAreaName}</span>
          </ReviewRow>
        )}
        {priorityLabel && (
          <ReviewRow>
            <span>Prioridade</span>
            <span>{priorityLabel}</span>
          </ReviewRow>
        )}
        {statusName && (
          <ReviewRow>
            <span>Status</span>
            <span>{statusName}</span>
          </ReviewRow>
        )}
        {justificationText && (
          <ReviewRow>
            <span>Justificativa</span>
            <span>{justificationText}</span>
          </ReviewRow>
        )}
      </ReviewBlock>

      {files && files.length > 0 && (
        <ReviewBlock>
          <ReviewBlockTitle>
            Anexos da resolução ({files.length} {files.length === 1 ? 'arquivo' : 'arquivos'})
          </ReviewBlockTitle>
          <FooterHint>Enviados ao WhatsApp do cliente</FooterHint>
          {files.map((fileItem) => (
            <FileItem key={fileItem.id}>
              <FileItemLeft>
                <FilePresent />
                <FileItemInfo>
                  <FileItemName>{fileItem.file.name}</FileItemName>
                  <FileItemSize>{toHumanFileSize(fileItem.file.size)}</FileItemSize>
                </FileItemInfo>
              </FileItemLeft>
              <FileItemLink
                href={fileItem.preview || URL.createObjectURL(fileItem.file)}
                target="_blank"
                rel="noreferrer"
              >
                <Eye />
                Visualizar
              </FileItemLink>
            </FileItem>
          ))}
        </ReviewBlock>
      )}

      <MessageCard data-testid="review-whatsapp-card">
        <MessageCardHeader>
          <MessageCardTitle>Resposta • WhatsApp</MessageCardTitle>
          <CommentTypeBadge type="WHATSAPP" label="só o cliente" />
        </MessageCardHeader>
        <WhatsappBubble>
          <WhatsappPreview
            clientName={clientName}
            ticketId={ticketId}
            agentComment={agentComment}
            normalizedStatusName={normalizedStatusName}
            hasFiles={hasFiles}
          />
        </WhatsappBubble>
      </MessageCard>

      {hasPublic && (
        <MessageCard data-testid="review-public-card">
          <MessageCardHeader>
            <MessageCardTitle>Resposta pública • Reclame Aqui</MessageCardTitle>
            <CommentTypeBadge type="PUBLIC_RA" label="visível a todos" />
          </MessageCardHeader>
          <MessageCardBody>{publicMessage}</MessageCardBody>
        </MessageCard>
      )}

      {hasReserved && (
        <MessageCard data-testid="review-reserved-card">
          <MessageCardHeader>
            <MessageCardTitle>Resposta privada • Reclame Aqui</MessageCardTitle>
            <CommentTypeBadge type="PRIVATE_RA" label="só o consumidor" bg="#DDDDDD" text="#4B4B4B" icon={<User />} />
          </MessageCardHeader>
          <MessageCardBody>{reservedMessage}</MessageCardBody>
        </MessageCard>
      )}

      <Footer>
        <FooterHint>
          {isReclameAqui
            ? 'Envia a mensagem no WhatsApp do cliente e publica a resposta no Reclame Aqui.'
            : 'Envia a mensagem no WhatsApp do cliente.'}
        </FooterHint>
        <Button
          rounded
          onClick={onResolve}
          loading={isLoading}
          disabled={isLoading}
          type="button"
          data-testid="ra-resolve-button"
        >
          Resolver
        </Button>
      </Footer>
    </StepBody>
  );
}

export default RaReviewStep;
